import { Fragment, ReactNode } from 'react';
import Link from 'next/link';
import Image from 'next/image';

import { articleCSS } from '@common/cssComponents';
import { H6, H1Small } from '@common/headline';
import { SignPost } from '@common/SignPost';
import { ReferencesRibbon } from '@common/ReferencesRibbon';

import CategoryNavigation from '@components/Resources/Category/CategoryNavigation';
import Comment, {
  COMMENTS_MAX_INDENTATION_LEVEL,
} from '@components/Resources/Article/Comment';
import CommentResponseForm from '@components/Resources/Article/CommentResponseForm';
import { Footer } from '@components/Footer/Footer';
import Meta from '@components/Meta';
import { NavBar } from '@components/NavBar/NavBar';
import SameCategoryArticles from '@components/Resources/Article/SameCategoryArticles';
import Sidebar from '@components/Resources/Article/Sidebar';

import {
  getAuthorURL,
  getMagazineFeaturedImageSize,
} from '@nextUtils/guidesUtils';

import { ArticleCommentsNodeType, ArticleProps } from '@nextTypes/guides';

import { articlePageStructuredData } from '../../../common/structuredData';
import useScrollPercentage from './Hooks/useScrollPercentage';
import SlideUpModal from './SlideUpModal';
import {
  getGuideType,
  shouldShowGuideModalBasedOnURL,
} from './Helpers/GuideHelpers';
import { getAbsoluteBaseURL } from '@nextUtils/getAbsoluteBaseURL';
import { ArticleCTA } from './ArticleCTA';

const estimateReadingTimeMinutes = (contentHTML: string) => {
  // Firstly strip all HTML tags
  const text = contentHTML
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const numWords = text.split(' ').length;

  // Use an average of 250 WPM (275 minus a bit for images as an approximation)
  // See: https://blog.medium.com/read-time-and-you-bc2048ab620c
  return Math.ceil(numWords / 250);
};

const getUtmMedium = (categorySlug: string) => {
  const indexOfForwardSlash = categorySlug.indexOf('/');

  const parentCategorySlug =
    indexOfForwardSlash === -1
      ? categorySlug
      : categorySlug.substring(0, indexOfForwardSlash);

  return parentCategorySlug.replace(/-/g, '_');
};

/**
 * Inserts CTA (Call to Action) components into the article content HTML.
 *
 * This function analyses the article content and strategically places CTA components
 * based on the structure of the content. It aims to insert CTAs at the beginning (after
 * the first H2 tag) and at the end of the article.
 */
const insertCTAIntoArticleContent = (
  contentHTML: string,
  utmMedium: string,
  utmCampaign: string,
  title: string,
): (string | JSX.Element)[] => {
  // NHSE are keen for us not display ads for the MSP on the blog post about the study results.
  const EXCLUDED_CTA_TITLES = [
    'New NHS study published on digital weight management programme',
    'Second Nature and Vitality to deliver medication weight-loss programme',
  ];

  if (EXCLUDED_CTA_TITLES.includes(title)) {
    return [contentHTML];
  }
  const contentSegments: (string | JSX.Element)[] = [];

  // Find all H2 elements in the content
  const h2Elements = Array.from(contentHTML.matchAll(/<h2/g));

  if (h2Elements.length < 1) {
    // If there are no H2 elements, add the entire content followed by a CTA at the end
    contentSegments.push(contentHTML);
    contentSegments.push(
      <ArticleCTA
        isTopCTA={false}
        utmMedium={utmMedium}
        utmCampaign={utmCampaign}
      />,
    );
  } else {
    // If there are H2 elements, insert a CTA after the first H2
    const indexOfFirstH2 = h2Elements[0].index;
    if (indexOfFirstH2) {
      // Add content before the first H2
      contentSegments.push(contentHTML.substring(0, indexOfFirstH2));
      // Insert top CTA
      contentSegments.push(
        <ArticleCTA
          isTopCTA={true}
          utmMedium={utmMedium}
          utmCampaign={utmCampaign}
        />,
      );
      // Add remaining content
      contentSegments.push(contentHTML.substring(indexOfFirstH2));
    } else {
      // If the H2 is at the very beginning, add all content
      contentSegments.push(contentHTML);
    }
    // Add bottom CTA
    contentSegments.push(
      <ArticleCTA
        isTopCTA={false}
        utmMedium={utmMedium}
        utmCampaign={utmCampaign}
      />,
    );
  }

  return contentSegments;
};

const parseCommentsTree = (
  postId: number,
  commentsTree: {
    nodes: ArticleCommentsNodeType[];
  },
  comments: ReactNode[],
  depth = 0,
  absoluteCanonicalURL: string,
) => {
  // We want to do a depth-first search so that we turn the comment tree into an ordered array
  // The comment nodes are not guaranteed to be in order, so sort in descending date order
  commentsTree.nodes.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  commentsTree.nodes.forEach(node => {
    // Skip over any comments pending moderator approval
    if (!node.approved) {
      return;
    }

    comments.push(
      <Comment
        commentId={node.commentId}
        content={node.content}
        date={node.date}
        indentationLevel={depth}
        key={comments.length}
        name={node.author.name}
        postId={postId}
        absoluteCanonicalURL={absoluteCanonicalURL}
      />,
    );

    if (depth >= COMMENTS_MAX_INDENTATION_LEVEL) {
      return;
    }

    // Recursively add all the child comments
    parseCommentsTree(
      postId,
      node.replies,
      comments,
      depth + 1,
      absoluteCanonicalURL,
    );
  });
};

const ArticlePage = ({
  allCategories,
  authorImageUrl,
  authorName,
  category,
  categorySlug,
  commentsTree,
  contentHTML,
  datePublished,
  featuredImageSizes,
  featuredImageUrl,
  featuredImageWidth,
  featuredImageHeight,
  metaDescription,
  postId,
  sameCategoryPosts,
  seoTitle,
  slug,
  title,
}: ArticleProps): JSX.Element => {
  const [articleRef, scrollPercentage] = useScrollPercentage();

  const utmMedium = getUtmMedium(categorySlug);
  const utmCampaign = slug.replace(/-/g, '_');

  const isStory = utmMedium === 'stories';
  const isBlog = utmMedium === 'blog';

  const GLP1_TERMS = [
    'glp1',
    'semaglutide',
    'wegovy',
    'ozempic',
    'rybelsus',
    'tirzepatide',
    'mounjaro',
    'liraglutide',
    'saxenda',
    'orlistat',
  ];

  // Add disclaimer to any GLP-1 guides or blog posts that have a GLP-1 related title
  if (
    categorySlug.endsWith('glp1s') ||
    GLP1_TERMS.some(term => slug.includes(term))
  ) {
    contentHTML = `${contentHTML}<div><i>Please note: We generally recommend speaking with your GP/doctor before you start taking any new medication like Mounjaro. If you do sign up for Second Nature's Mounjaro programme, then we'll also send your GP a letter to let them know you've started a medication-supported weight-loss journey.</i></div>`;
  }

  const contentSegments =
    isStory || utmMedium === 'recipes'
      ? [contentHTML] // don't insert CTA for stories and recipes
      : insertCTAIntoArticleContent(contentHTML, utmMedium, utmCampaign, title);

  const canonicalSlug =
    category === 'Local' // For Local use one forward slash in the slug link, e.g. bristol/weight-watchers
      ? slug.replace('-', '/')
      : slug;

  const relativeCanonicalURL = `${
    isStory || isBlog ? '' : '/guides'
  }/${categorySlug}/${canonicalSlug}`;

  const baseUrl = getAbsoluteBaseURL();
  const absoluteCanonicalURL = `${baseUrl}${relativeCanonicalURL}`;

  const comments: ReactNode[] = [];
  parseCommentsTree(postId, commentsTree, comments, 0, absoluteCanonicalURL);

  const sameCategoryPostsWithoutThisPost = sameCategoryPosts.edges.filter(
    post => post.node.slug !== slug,
  );

  const imageAspectRatio = featuredImageWidth / featuredImageHeight;
  const imageWidth = 430;
  const imageHeight = imageWidth / imageAspectRatio;

  const pageTitle = seoTitle || title;

  // For blog posts, we don't want to set the dateModified,
  // but for guides we want to set it to the first of the month
  const dateModified = isBlog
    ? undefined
    : new Date(new Date().getFullYear(), new Date().getMonth(), 1);

  return (
    <>
      <Meta
        title={pageTitle}
        metaDescription={metaDescription}
        ogImageUrl={getMagazineFeaturedImageSize(featuredImageSizes).sourceUrl}
        /* Magazine is fixed 710x375 size */
        ogImageWidth={710}
        ogImageHeight={375}
        relativeCanonicalURL={relativeCanonicalURL}
        structuredData={articlePageStructuredData(
          pageTitle,
          new Date(datePublished),
          featuredImageUrl,
          authorName,
          getAuthorURL(authorName, true),
          dateModified,
        )}
      />
      {isStory || isBlog ? (
        <div className="pb-[64px]">
          <NavBar />
        </div>
      ) : (
        <>
          <NavBar utmMedium={utmMedium} utmCampaign={`${utmCampaign}_navbar`} />
          <CategoryNavigation allCategories={allCategories} />
        </>
      )}
      <div className="px-3 py-4 text-blue lg:container lg:m-auto lg:px-0 lg:py-8">
        <div className="flex justify-center">
          <div className="main-container flex max-w-[680px] flex-col overflow-x-auto md:flex-row md:flex-wrap">
            <div className="w-full">
              {isBlog && (
                <Link
                  href="/blog"
                  className="mb-2 inline-block uppercase md:mb-4"
                >
                  Blog
                </Link>
              )}
              {isStory && (
                <Link
                  href="/stories"
                  className="mb-2 inline-block uppercase md:mb-4"
                >
                  Stories
                </Link>
              )}
              {!isBlog && !isStory && (
                <Link
                  href={
                    category === 'Local'
                      ? '/guides/local'
                      : `/guides/category/${categorySlug}`
                  }
                  className="mb-2 inline-block uppercase md:mb-4"
                >
                  <H6>{category}</H6>
                </Link>
              )}
              <H1Small>{title}</H1Small>
            </div>
            <div className="order-3 w-[250px] max-w-[320px] pr-5 md:order-none md:ml-auto md:mt-7">
              <Sidebar
                absoluteCanonicalURL={absoluteCanonicalURL}
                authorImageUrl={authorImageUrl}
                authorName={authorName}
                dateModified={dateModified}
                datePublished={datePublished}
                isBlog={isBlog}
                isStory={isStory}
                readingTime={estimateReadingTimeMinutes(contentHTML)}
              />
            </div>
            <div className="mt-5 flex-1 md:mt-7">
              <Image
                src={featuredImageUrl}
                width={imageWidth}
                height={imageHeight}
                className="w-full"
                alt="title"
                priority
              />
            </div>
            <div
              // article-content is set globally to allow the styling of the article content to be overridden
              // eslint-disable-next-line tailwindcss/no-custom-classname
              className="mb-12 font-cera text-[16px] font-normal leading-[26px] tracking-[-0.22px] text-blue md:order-3 md:mb-16 md:text-[18px] md:leading-[30px]"
              ref={articleRef}
            >
              {contentSegments.map((segment, index) =>
                typeof segment === 'string' ? (
                  <div
                    key={index}
                    className="article-content"
                    dangerouslySetInnerHTML={{ __html: segment }}
                  />
                ) : (
                  // We don't apply the "article-content" class to the Fragment because:
                  // 1. It contains a React component (ArticleCTA) that has its own styling
                  // 2. We want to avoid the article-content styles affecting the CTA's appearance
                  // 3. The Fragment allows us to render the CTA without an additional wrapping div
                  <Fragment key={index}>{segment}</Fragment>
                ),
              )}
            </div>
            {shouldShowGuideModalBasedOnURL(absoluteCanonicalURL) && (
              <SlideUpModal
                guideType={getGuideType(absoluteCanonicalURL)}
                scrollPercentage={scrollPercentage}
                guideCategory={category}
                guideTitle={title}
              />
            )}
          </div>
        </div>
        <SameCategoryArticles posts={sameCategoryPostsWithoutThisPost} />
      </div>
      <SignPost version="A" utmMedium={utmMedium} utmCampaign={utmCampaign} />
      <div className="bg-dawn-blue-100">
        <div className="p-3 lg:container lg:m-auto lg:px-0">
          {/* Using this anchor div for links in comments pagination */}
          <div id="comments" />
          <CommentResponseForm
            postId={postId}
            absoluteCanonicalURL={absoluteCanonicalURL}
          />
          {comments}
        </div>
      </div>
      <ReferencesRibbon />
      <Footer />
      <style jsx>{articleCSS}</style>
    </>
  );
};

export default ArticlePage;
